import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { Link, LinkProps } from 'react-router-dom';
import Wallet from '../Wallet';
import { StyledNavLink, Box, BurgerIcon, Flex, EBLogo, DropdownItem } from 'components/ui';
import { layout } from 'styled-system';
import twitterIcon from 'img/twitter.svg';
import emailIcon from 'img/email.svg';
import openseaIcon from 'img/opensea-circle.svg';
import { twitterURL, contactEmail, openseaSocialLink } from 'utils/config';
import { useWallet } from 'components/WalletContext';
import { useDropsContext } from 'components/Drops/DropsContext';
import { useSettingsContext } from 'components/SettingsContext';
import ContractsList from 'components/ContractsList';
import { useDetectClickOutside } from 'react-detect-click-outside';
import { RemoveScroll } from 'react-remove-scroll';
import eulerBeatsLogoText from 'img/eb-text.png';
import { SCREEN_SIZES } from 'utils/styles';

const Wrapper = styled.div`
  width: 100%;
  position: relative;
  z-index: 99;
  box-shadow: 0px -6px 10px rgba(0, 0, 0, 0.8), 0px 4px 15px rgba(0, 0, 0, 0.05);
  background-color: rgba(0, 0, 0, 0.5);

  a {
    text-decoration: none;
  }
`;

const Main = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin: 0 auto;
`;

const Navigation = styled.div`
  display: flex;
  align-items: center;
  height: 26px;

  & > a {
    color: black;
    padding: 12px;
  }

  ${layout};
`;

const MobileNav: any = styled.div<any>`
  width: 240px;
  height: 100vh;
  background-color: #333;
  color: white;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  right: -400px;
  padding: 32px;
  z-index: 4;
  transition: 0.3s all ease-in-out;
  font-size: 16px;

  a {
    display: block;
    padding: 0;
    margin-bottom: 16px;
    color: white !important;
  }

  ${props =>
    props.open &&
    `
  right: 0;
  box-shadow: 0 8px 5px 5px rgba(0, 0, 0, .3);
`}
`;

const SocialLinks = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 40px;
`;

const StyledLink = styled.a`
  color: ${props => props.theme.colors.copy} !important;
`;

const MobileLink = ({ text, ...props }: LinkProps & { text: string }) => (
  <Link {...props}>{text}</Link>
);

const MenuOptions = ({ StyledNavComponent, connected, selectWallet, ...rest }: any) => (
  <>
    <DropdownItem title="Drops" breakpoint={SCREEN_SIZES.sm}>
      <Flex flexDirection="column">
        <StyledNavComponent
          to="/futura/gallery"
          text="Futura"
          {...rest}
          isActive={(match, { pathname }) => match || pathname.includes('futura')}
        />
        <StyledNavComponent
          to="/enigma"
          text="Enigma"
          {...rest}
          isActive={(match, { pathname }) => match || pathname.includes('enigma')}
        />
        <StyledNavComponent
          to="/genesis"
          text="Genesis"
          {...rest}
          isActive={(match, { pathname }) => match || pathname.includes('genesis')}
        />
      </Flex>
    </DropdownItem>
    <StyledNavComponent to="/stake" text="Stake" {...rest} />
    <StyledLink href="https://eulerbeats.gitbook.io/eulerbeats-docs/" target="_blank" {...rest}>
      Docs
    </StyledLink>
    <StyledLink {...rest} href="/about">
      About
    </StyledLink>
    <StyledLink {...rest} href="/contact">
      Contact
    </StyledLink>
  </>
);

const FooterOptions = () => (
  <>
    <MobileLink to="/terms-of-service" text="Terms of Service" />
    <StyledLink
      color="copy"
      href="https://eulerbeatsprod.blob.core.windows.net/miscellaneous/grants-program-terms.pdf"
      target="_blank"
    >
      Grant Program Terms
    </StyledLink>

    <ContractsList />

    <SocialLinks>
      <a href={openseaSocialLink} target="_blank" rel="noopener noreferrer">
        <img src={openseaIcon} alt="OpenSea" width="28px" color="white" />
      </a>
      <a href={twitterURL} target="_blank" rel="noopener noreferrer">
        <img src={twitterIcon} alt="@eulerbeats" width="28px" />
      </a>
      <a href={`mailto:${contactEmail}`} target="_blank" rel="noopener noreferrer">
        <img src={emailIcon} alt={contactEmail} width="28px" />
      </a>
    </SocialLinks>
  </>
);

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { address, selectWallet } = useWallet();
  const { dropConfig } = useDropsContext();
  const { setShowSettings } = useSettingsContext();

  const ref = useDetectClickOutside({
    onTriggered: () => setMenuOpen(false),
  });

  return (
    <Wrapper>
      <Box p={['16px', '16px 40px']} zIndex={0} border="none" fontSize="16">
        <Main>
          <Box width={['auto', 1 / 3, 1 / 4]}>
            <Link to="/">
              <Flex alignCenter>
                <EBLogo width="33px" />
                <Box mt="6px" ml={3} display={['none', 'none', 'none', 'inline-block']}>
                  <img src={eulerBeatsLogoText} height="18px" alt="EulerBeats" />
                </Box>
              </Flex>
            </Link>
          </Box>

          {/* DESKTOP NAV LINKS */}
          <Flex display={['none', 'none', 'flex']} width={[0, 4 / 5]} justifyCenter>
            <Navigation>
              <MenuOptions
                connected={!!address}
                selectWallet={selectWallet}
                StyledNavComponent={StyledNavLink}
                activeBg={dropConfig.theme.activeLink}
              />
            </Navigation>
          </Flex>

          <Flex alignItems="center" width={['auto', 1 / 3, 1 / 4]} justifyContent="flex-end">
            <Wallet />

            {/* MOBILE NAV LINKS */}
            <Flex ref={ref} display={['flex', 'flex', 'none']} width={['auto', 1 / 3, 1 / 3]}>
              <BurgerIcon
                open={menuOpen}
                onToggle={() => {
                  setShowSettings(false);
                  setMenuOpen(!menuOpen);
                }}
              />

              <MobileNav open={menuOpen}>
                {menuOpen && (
                  <RemoveScroll>
                    <MenuOptions
                      connected={!!address}
                      selectWallet={selectWallet}
                      StyledNavComponent={MobileLink}
                    />
                    <FooterOptions />
                  </RemoveScroll>
                )}
              </MobileNav>
            </Flex>
          </Flex>
        </Main>
      </Box>
    </Wrapper>
  );
};

export default Header;
